import React, { useCallback, useEffect, useMemo } from 'react'
import { Button, CardRadioInput, Checkbox, Info } from '@demandlocal/components'
import { motion } from 'framer-motion'
import { FieldValues, useFormContext } from 'react-hook-form'
import { useAtom } from 'jotai'
import { createCampaignFormValuesAtom } from './CreateNewCampaignForm'
import { ReactComponent as NonInventoryIcon } from '../../../images/icons/non_inventory_campaign.svg'
import { ReactComponent as InventoryIcon } from '../../../images/icons/inventory_campaign.svg'
import { ReactComponent as UploadIcon } from '../../../images/icons/upload_with_cloud.svg'
import { ReactComponent as FacebookIcon } from '../../../images/icons/facebook_with_cloud.svg'
import { ReactComponent as GoogleIcon } from '../../../images/icons/google_with_cloud.svg'
import { ReactComponent as ISEMIcon } from '../../../images/icons/chart_with_cloud.svg'
import { ReactComponent as MobileIcon } from '../../../images/icons/mobile_product.svg'
import { ReactComponent as OttIcon } from '../../../images/icons/ott_product.svg'
import { ReactComponent as VideoIcon } from '../../../images/icons/video_product.svg'
import { ReactComponent as AudioIcon } from '../../../images/icons/audio_product.svg'
import { useGetOverallStats } from '../../../api/dealership/useGetOverallStats'
import LoadingComponent from '../../../components/LoadingComponent'
import { ReactComponent as DynamicDisplayIcon } from '../../../images/icons/target_with_cloud.svg'
import { useGetIncompleteCampaignsByDealershipId } from '../../../api/dealership/campaigns/useGetIncompleteCampaignsByDealershipId'
import { useDeleteLinkCampaign } from '../../../api/dealership/campaigns/useDeleteLinkCampaign'
import { lowerCase } from 'lodash'
import { useDeleteLinkCampaignCreatives } from '../../../api/dealership/campaigns/useDeleteLinkCampaignCreatives'
import { useLocation, useNavigate, useParams } from 'react-router'
import { toast } from 'react-hot-toast'
import { Link } from 'react-router-dom'
import InfoMessage from '../../../components/InfoMessage'
import CampaignProgressBar from '../../../components/CampaignProgressBars/CampaignProgressBar'

type Props = {
  step: number
  setStep: (step: number) => void
}

const CreateNewCampaignStepOne = ({ step, setStep }: Props): JSX.Element => {
  const formContext = useFormContext()
  const { handleSubmit, reset, watch } = formContext
  const navigate = useNavigate()
  const location = useLocation()
  const { data: dealershipStats, isLoading } = useGetOverallStats()
  const [, setFormValues] = useAtom(createCampaignFormValuesAtom)
  const { data: incompleteCampaign } = useGetIncompleteCampaignsByDealershipId({})
  const { mutateAsync: deleteLinkCampaign } = useDeleteLinkCampaign()
  const { mutateAsync: deleteLinkCreatives } = useDeleteLinkCampaignCreatives()
  const { agency_id, dealership_id } = useParams()

  const products = useMemo(() => {
    return {
      mob: {
        product_code: 'mob',
        product_name: 'Mobile Display',
        product_description:
          'Reach and engage your target audience on the go with mobile advertising, capturing their attention through personalized and location-based targeting.',
        icon: <MobileIcon />,
      },
      ott: {
        product_code: 'ott',
        product_name: 'TV (OTT/CTV)',
        product_description: `Extend your campaign's reach beyond traditional TV channels and connect with viewers through Over-the-Top (OTT) advertising, delivering your message on streaming platforms to a highly engaged audience.`,
        icon: <OttIcon />,
      },
      vid: {
        product_code: 'vid',
        product_name: 'Video (Preroll)',
        product_description: `Drive brand awareness and engagement with video preroll ads, capturing viewers' attention before they watch their desired online video content and delivering your message effectively.`,
        icon: <VideoIcon />,
      },
      aud: {
        product_code: 'aud',
        product_name: 'Digital Audio',
        product_description: `Tap into the power of audio advertising, leveraging the popularity of podcasts, music streaming platforms, and digital radio to connect with listeners, enhance brand recall, and create a memorable auditory experience for your campaign.`,
        icon: <AudioIcon />,
      },
      vla: {
        product_code: 'vla',
        product_name: 'Google Vehicle Ads',
        product_description: `Spotlight your most relevant inventory in both text AND image ads with Google's automotive specific shopping ads! Powered by Google's ability to match user generated search queries against your most relevant, up to date inventory. The beauty of Google's Vehicle Ads is the ability to display ALL of your vehicles that match the search query in one ad, meaning multiple Google Vehicle listings in one search results page. These ads are in addition to any existing Google Search campaigns, meaning they can run simultaneously with SEM ads already running, allowing multiple ad placements from multiple angles on a single Search Results Query`,
        icon: <GoogleIcon />,
      },
      dyn: {
        product_code: 'dyn',
        product_name: 'Facebook Dynamic',
        product_description: `Generate high-quality leads and drive them directly to the VDPs of the cars they are interested in buying by using Facebook's in-market auto buyer audience segments, or, by targeting people who have visited or shown interest in your vehicle detail pages (VDPs).`,
        icon: <FacebookIcon />,
      },
      inv: {
        product_code: 'inv',
        product_name: 'Inventory Search Marketing',
        product_description: `Based on daily inventory feeds, create custom paid search campaigns for each vehicle in the dealer's inventory that day. When a shopper searches model specific terms that match a vehicle in the dealer's inventory, a custom SEM ad featuring that vehicle's year, make and model details populates. Clicks drive the consumer to the dealer’s VDP or SRP of the vehicle they were searching for. A custom ad group is dynamically generated for each vehicle in the inventory feed and is updated daily.`,
        icon: <ISEMIcon />,
      },
      dyd: {
        product_code: 'dyd',
        product_name: 'Dynamic Inventory Display',
        product_description: `Serve dynamic inventory based display banners in front of local, lower funnel, in-market autoshoppers. Target customer based on their online shopping behaviors, actual dealership site visits, and your first party customer database. Only serve the images of the inventory the dealer has in stock.`,
        icon: <DynamicDisplayIcon />,
      },
    }
  }, [])

  const inventoryProducts = useMemo(() => {
    return ['inv', 'vla', 'dyn', 'dyd']
  }, [])

  useEffect(() => {
    async function deleteCampaign(campaign_id: number): Promise<void> {
      await deleteLinkCampaign({ id: campaign_id })
    }
    async function deleteCreatives(campaign_id: number): Promise<void> {
      await deleteLinkCreatives({ id: campaign_id })
    }
    if (!isLoading && incompleteCampaign) {
      const continuePreviousCampaign = window.confirm(
        'Do you want to continue with your unfinished campaign?',
      )

      if (continuePreviousCampaign) {
        const isInventoryCampaign = inventoryProducts.includes(
          lowerCase(incompleteCampaign.product_code),
        )
        setFormValues({
          campaign_type: isInventoryCampaign ? 'inventory' : 'non_inventory',
          pacing_id: incompleteCampaign.pacing_id,
          campaign_id: incompleteCampaign.link_campaign_id,
          campaign_name: incompleteCampaign.campaign_display_name,
          notes: incompleteCampaign.notes,
          start_date: incompleteCampaign.start_date,
          end_date: incompleteCampaign.end_date,
          budget: incompleteCampaign.budget,
          objective: incompleteCampaign.objective,
          target: {
            zip: incompleteCampaign.zip,
            audience: [],
          },
          ...(isInventoryCampaign
            ? {
                inventory: {
                  details: {
                    allowed_vehicle_states: incompleteCampaign.allowed_vehicle_states,
                  },
                  filters: {
                    allowed_makes: incompleteCampaign.allowed_makes,
                    disallowed_makes: incompleteCampaign.disallowed_makes,
                    allowed_models: incompleteCampaign.allowed_models,
                    disallowed_models: incompleteCampaign.disallowed_models,
                    allowed_body_substrings: incompleteCampaign.allowed_body_substrings,
                    disallowed_body_substrings:
                      incompleteCampaign.disallowed_body_substrings,
                    earliest_allowed_year: incompleteCampaign.earliest_allowed_year,
                    latest_allowed_year: incompleteCampaign.latest_allowed_year,
                    lowest_allowed_price: incompleteCampaign.lowest_allowed_price,
                    highest_allowed_price: incompleteCampaign.highest_allowed_price,
                    lowest_allowed_mileage: incompleteCampaign.lowest_allowed_mileage,
                    highest_allowed_mileage: incompleteCampaign.highest_allowed_mileage,
                    allowed_stock_number_prefixes:
                      incompleteCampaign.allowed_stock_number_prefixes,
                    disallowed_stock_number_prefixes:
                      incompleteCampaign.disallowed_stock_number_prefixes,
                    minimum_age_days: incompleteCampaign.minimum_age_days,
                    maximum_age_days: incompleteCampaign.maximum_age_days,
                    no_price_only: false,
                    exclude_if_no_price: false,
                  },
                },
              }
            : {}),
          product: {
            product_code:
              products[
                lowerCase(incompleteCampaign.product_code) as keyof typeof products
              ].product_code,
            product_name:
              products[
                lowerCase(incompleteCampaign.product_code) as keyof typeof products
              ].product_name,
            product_description:
              products[
                lowerCase(incompleteCampaign.product_code) as keyof typeof products
              ].product_description,
            icon: products[
              lowerCase(incompleteCampaign.product_code) as keyof typeof products
            ].icon,
            creatives:
              incompleteCampaign.creatives && incompleteCampaign.creatives?.split(','),
          },
        })

        setStep(3)
      } else {
        // if user doesn't want to continue with previous campaign, delete it and the creatives
        deleteCampaign(incompleteCampaign.link_campaign_id)
        deleteCreatives(incompleteCampaign.link_campaign_id)
      }
    }
  }, [
    incompleteCampaign,
    reset,
    setStep,
    isLoading,
    setFormValues,
    deleteLinkCampaign,
    products,
    inventoryProducts,
    deleteLinkCreatives,
  ])

  const termsValue = watch('terms')

  const onSubmit = useCallback(
    (values: Partial<FieldValues>): void => {
      if (!values.campaign_type) {
        setStep(1)
        return
      }
      if (!values.terms) {
        toast.error('Please accept the Terms of Service before continuing.')
        return
      }
      setFormValues({
        campaign_type: values.campaign_type,
      })
      setStep(2) // change to step 2 when done
    },
    [setFormValues, setStep],
  )

  const submitOnEnter = useCallback(
    (e): void => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSubmit(onSubmit)()
      }
    },
    [handleSubmit, onSubmit],
  )

  useEffect(() => {
    document.addEventListener('keydown', submitOnEnter)
    return () => document.removeEventListener('keydown', submitOnEnter)
  }, [submitOnEnter])

  if (isLoading) return <LoadingComponent title="Loading..." />

  return (
    <motion.form onSubmit={handleSubmit(onSubmit)} className={'flex flex-1 flex-col'}>
      <div className={'flex flex-col gap-5 overflow-auto px-12'}>
        <div className={'flex flex-1 flex-row py-10 '}>
          <div className={'flex-1 grid grid-cols-2 gap-5 h-fit'}>
            <CardRadioInput
              icon={<NonInventoryIcon />}
              name={'campaign_type'}
              value={'non_inventory'}
              label={'Mobile, TV, Audio'}
              description={
                'Leverage the power of the visual appeal of mobile, the immersive reach of television, and the persuasive strength of audio to reach your target audience.'
              }
              background={'white'}
            />
            {dealershipStats?.totalVehicles && dealershipStats.totalVehicles > 0 ? (
              <CardRadioInput
                icon={<InventoryIcon />}
                name={'campaign_type'}
                value={'inventory'}
                label={'Campaign using my current inventory'}
                description={'Create ads from your inventory'}
                background={'white'}
                descriptionBox={{
                  title: 'Inventory size',
                  value: dealershipStats?.totalVehicles,
                }}
              />
            ) : (
              <CardRadioInput
                icon={<UploadIcon />}
                name={'campaign_type'}
                value={'import'}
                label={'Import my inventory'}
                description={
                  <div className="flex flex-col gap-1">
                    <span>
                      Add your inventory in a few easy steps. You havent connected your
                      inventory yet, so this option is unavailable.
                    </span>
                    <Link
                      className="text-primary-persian-blue font-bold cursor-pointer"
                      to={`/dashboard/agency/${agency_id}/dealership/${dealership_id}/inventory`}>
                      Connect it here!
                    </Link>
                  </div>
                }
                background={'white'}
                disabled
              />
            )}
          </div>
        </div>
        <div>
          <InfoMessage
            content={
              <div className="flex flex-col gap-5">
                <span>
                  No worries! You can <strong>skip steps</strong> if you don’t have the
                  information at hand. Your progress is{' '}
                  <strong>saved automatically</strong> as you progress.
                </span>
                <span>
                  You can find your <strong>new campaign draft</strong> in your campaigns
                  page.
                </span>
              </div>
            }
          />
        </div>
        <div className={'border-t-2 py-5'}>
          <div
            className={
              'flex flex-row items-center bg-secondary-yellow-100 w-full self-center py-3 text-sm bg-yellow-secondary/10'
            }>
            <span className={'px-3'}>
              <Checkbox
                name={`terms`}
                label={
                  <div className={'text-xs'}>
                    By continuing with this campaign, you agree to follow our{' '}
                    <Link to={`/terms?next=${location.pathname}`}>
                      <strong className={'font-bold text-primary-persian-blue'}>
                        Terms of Services
                      </strong>
                    </Link>
                    , indicating that you have read and understood them beforehand.
                  </div>
                }
              />
            </span>
          </div>
        </div>
      </div>

      <motion.div
        layout
        layoutId={'buttons-container'}
        className={
          'h-20 items-center flex justify-between border-t-2 py-4 px-12 absolute bottom-0 w-full bg-white'
        }>
        <Button
          variant={'secondary'}
          onClick={(e) => {
            e.preventDefault()
            navigate('..')
          }}>
          Back
        </Button>
        <CampaignProgressBar step={step} />
        <div className="flex flex-row gap-1">
          {!termsValue && <Info message="You must accept the terms to continue" />}
          <Button variant={'primary'} disabled={!termsValue}>
            Continue
          </Button>
        </div>
      </motion.div>
    </motion.form>
  )
}

export default CreateNewCampaignStepOne
